import { useCallback, useEffect, useRef, useState } from "react";

import { Alert, AlertDescription } from "./base";

// Shared by the FT admin pages (Jobs/Deployments/Models/Evals): their mutations (cancel job,
// terminate deployment, retire model, cancel eval) used to fail silently — an admin could click
// Terminate, get a 4xx/5xx, and walk away believing the GPU was released. The hook holds one
// notice per detail panel; errors persist until the next attempt (or row switch), successes
// auto-dismiss once the status pill has had time to reflect the change.

export type MutationNotice = { kind: "success" | "error"; message: string };

const SUCCESS_DISMISS_MS = 5000;

export const useMutationNotice = () => {
  const [notice, setNotice] = useState<MutationNotice | null>(null);
  const timerRef = useRef<number | null>(null);

  const stopTimer = () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const clearNotice = useCallback(() => {
    stopTimer();
    setNotice(null);
  }, []);

  const showError = useCallback((e: unknown, fallback: string) => {
    stopTimer();
    // makeRequest surfaces the server's error/detail field (falling back to status text)
    const message = e instanceof Error && e.message ? e.message : fallback;
    setNotice({ kind: "error", message });
  }, []);

  const showSuccess = useCallback((message: string) => {
    stopTimer();
    setNotice({ kind: "success", message });
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setNotice(null);
    }, SUCCESS_DISMISS_MS);
  }, []);

  // don't fire the auto-dismiss setState after unmount
  useEffect(() => stopTimer, []);

  return { notice, showError, showSuccess, clearNotice };
};

export const FineTuningMutationNotice = ({ notice, className }: { notice: MutationNotice | null; className?: string }) => {
  if (!notice) return null;
  return (
    <div className={className}>
      <Alert variant={notice.kind === "error" ? "destructive" : "success"}>
        <AlertDescription className="text-sm break-words">{notice.message}</AlertDescription>
      </Alert>
    </div>
  );
};
